import React, { useState, useEffect, useRef } from 'react';
import { BsPinAngle, BsPinAngleFill, BsFullscreen, BsFullscreenExit } from "react-icons/bs";
import { User } from 'firebase/auth';
import { generateChatCompletion } from '../../services/openaiService';
import ToolHeader from './ToolHeader';
import './NotesTab.css';

interface NotesTabProps {
  user: User | null;
  hasPaidSubscription: boolean;
  onShowPaymentPopup: () => void;
  onShowLoginInfo: () => void;
  isWalkthroughMode?: boolean;
  isPinned: boolean;
  onTogglePin: (pinned: boolean) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: (fullscreen: boolean) => void;
}

const NotesTab: React.FC<NotesTabProps> = ({
  user,
  hasPaidSubscription,
  onShowPaymentPopup,
  onShowLoginInfo,
  isWalkthroughMode = false,
  isPinned,
  onTogglePin,
  isFullscreen,
  onToggleFullscreen
}) => {
  // Plain text version of welcome message for typewriter effect
  const welcomeMessagePlain = `welcome to lofizone.space

for the best experience, please download our chrome extension

it'll guarantee you a daily dose of lofi beats and relaxing visuals

p.s. this is our new notepad, great for scribbling down ideas while you code

happy coding :)`;

  // HTML version with link for final display
  const welcomeMessageWithLink = `welcome to lofizone.space

for the best experience, please <a href="https://github.com/wmastover/VybecodeChromeExtension" target="_blank" rel="noopener noreferrer">download</a> our chrome extension

it'll guarantee you a daily dose of lofi beats and relaxing visuals

p.s. this is our new notepad, great for scribbling down ideas while you code

happy coding :)`;

  // Note-related state
  const [noteContent, setNoteContent] = useState('');
  const [hasNote, setHasNote] = useState(false);
  const [isTypingAnimation, setIsTypingAnimation] = useState(false);
  const [slashCommand, setSlashCommand] = useState<{ command: string, suggestion: string, range: Range | null, position: { x: number, y: number } } | null>(null);
  
  // State for typewriter effect
  const [typewriterText, setTypewriterText] = useState('');
  const [typewriterIndex, setTypewriterIndex] = useState(0);
  
  // Note-related refs
  const noteInputRef = useRef<HTMLDivElement>(null);

  // Load initial content into the contenteditable div
  useEffect(() => {
    if (noteInputRef.current && noteContent && !noteInputRef.current.innerHTML) {
      // Set the HTML content from localStorage
      noteInputRef.current.innerHTML = noteContent;
      
      // Make sure checkboxes have proper event listeners
      const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          // Save the updated content whenever a checkbox changes
          if (noteInputRef.current) {
            setNoteContent(noteInputRef.current.innerHTML);
            localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
          }
        });
      });
    }
  }, [noteContent, noteInputRef.current]);

  // Load saved note data on initial render
  useEffect(() => {
    // Check if it's the first visit
    const isFirstVisit = false; // Disabled default welcome message
    
    if (isFirstVisit) {
      // Mark that user has visited before
      localStorage.setItem('vibeFirstVisit', 'false');
      
      // Set welcome message - start with empty and use typewriter effect
      setIsTypingAnimation(true);
      setTypewriterIndex(0);
      setTypewriterText('');
    } else {
      // Load note content
      const savedNoteContent = localStorage.getItem('vibeNote');
      if (savedNoteContent) {
        setNoteContent(savedNoteContent);
        setHasNote(savedNoteContent.trim().length > 0);
        
        // Only attempt to update the DOM if we have proper access to features
        if (isWalkthroughMode || (user && hasPaidSubscription)) {
          // Force a content update if the ref is available
          if (noteInputRef.current) {
            noteInputRef.current.innerHTML = savedNoteContent;
            
            // Re-establish event listeners for checkboxes
            const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
            checkboxes.forEach(checkbox => {
              checkbox.addEventListener('change', () => {
                if (noteInputRef.current) {
                  const updatedContent = noteInputRef.current.innerHTML;
                  setNoteContent(updatedContent);
                  localStorage.setItem('vibeNote', updatedContent);
                }
              });
            });
          }
        }
      }
    }
  }, [user, hasPaidSubscription, isWalkthroughMode]);
  
  // Additional effect to reload note content when authentication/subscription status changes
  useEffect(() => {
    // Only proceed if user is logged in and has a paid subscription
    if ((user && hasPaidSubscription) || isWalkthroughMode) {
      // Get the saved note content from localStorage
      const savedNoteContent = localStorage.getItem('vibeNote');
      if (savedNoteContent && savedNoteContent.trim().length > 0) {
        // Update the state
        setNoteContent(savedNoteContent);
        setHasNote(true);
        
        // Force a content update if the ref is available
        if (noteInputRef.current) {
          // Set the content
          noteInputRef.current.innerHTML = savedNoteContent;
          
          // Re-establish event listeners for checkboxes
          const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
          checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', () => {
              if (noteInputRef.current) {
                const updatedContent = noteInputRef.current.innerHTML;
                setNoteContent(updatedContent);
                localStorage.setItem('vibeNote', updatedContent);
              }
            });
          });
        }
      }
    }
  }, [user, hasPaidSubscription, isWalkthroughMode]);
  
  // Update the typewriter effect to use the plain text version
  useEffect(() => {
    if (isTypingAnimation && typewriterIndex < welcomeMessagePlain.length) {
      const timer = setTimeout(() => {
        setTypewriterText(prev => prev + welcomeMessagePlain.charAt(typewriterIndex));
        setTypewriterIndex(prev => prev + 1);
      }, 50); // Adjust speed as needed
      
      return () => clearTimeout(timer);
    } else if (isTypingAnimation && typewriterIndex >= welcomeMessagePlain.length) {
      // Animation completed
      setIsTypingAnimation(false);
      
      // Use the HTML version with link for final content
      const formattedWelcomeMessage = welcomeMessageWithLink.replace(/\n/g, '<br>');
      
      // Save the welcome message as the note content
      setNoteContent(formattedWelcomeMessage);
      setHasNote(true);
      
      // Save to localStorage
      localStorage.setItem('vibeNote', formattedWelcomeMessage);
    }
  }, [isTypingAnimation, typewriterIndex, welcomeMessagePlain, welcomeMessageWithLink]);
  
  // Save note content to localStorage whenever it changes
  useEffect(() => {
    if (noteContent) {
      localStorage.setItem('vibeNote', noteContent);
    }
  }, [noteContent]);
  
  // Add event listener to save note content when tab is closed
  useEffect(() => {
    const saveNoteOnUnload = () => {
      // Get the most up-to-date content directly from the DOM
      if (noteInputRef.current) {
        // Before saving, ensure all checkbox states are reflected in their attributes
        const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
          // Cast checkbox to HTMLInputElement to access 'checked' property
          const inputCheckbox = checkbox as HTMLInputElement;
          if (inputCheckbox.checked) {
            checkbox.setAttribute('checked', 'checked');
          } else {
            checkbox.removeAttribute('checked');
          }
        });
        
        const currentContent = noteInputRef.current.innerHTML;
        if (currentContent) {
          localStorage.setItem('vibeNote', currentContent);
          
          // Force a sync localStorage flush by reading a value back
          // This helps ensure the data is properly saved in some browsers
          localStorage.getItem('vibeNote');
        }
      }
    };
    
    // Add multiple event listeners to catch all possible exit scenarios
    window.addEventListener('beforeunload', saveNoteOnUnload);
    window.addEventListener('unload', saveNoteOnUnload);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        saveNoteOnUnload();
      }
    });
    
    // Save every 10 seconds as an extra precaution
    const autoSaveInterval = setInterval(saveNoteOnUnload, 10000);
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', saveNoteOnUnload);
      window.removeEventListener('unload', saveNoteOnUnload);
      document.removeEventListener('visibilitychange', saveNoteOnUnload);
      clearInterval(autoSaveInterval);
    };
  }, []);
  
  // Handle changes in the contenteditable div
  const handleContentEditableChange = (e: React.FormEvent<HTMLDivElement>) => {
    const content = e.currentTarget.innerHTML;
    setNoteContent(content);
    setHasNote(content.trim().length > 0);
    
    // Check for slash commands
    const commandProcessed = checkForSlashCommands(e.currentTarget);
    
    // Clear slash command suggestion if a command was processed
    if (commandProcessed) {
      setSlashCommand(null);
    }
  };
  
  // Handle slash commands
  const checkForSlashCommands = (element: HTMLDivElement) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    
    // We're interested in text nodes (nodeType === 3)
    if (node.nodeType !== 3) return false;
    
    const text = node.textContent || '';
    const cursorPosition = range.startOffset;
    
    // Check for slash commands like /todo
    const slashPattern = /\/todo\s$|\/prompt\s$/;
    
    // Get text before cursor
    const textBeforeCursor = text.substring(0, cursorPosition);
    
    // Check if a slash command was just completed (with a space)
    const match = textBeforeCursor.match(slashPattern);
    
    if (match) {
      // Get the matched command
      const command = match[0].trim();
      
      // Process the command
      return processSlashCommand(command, node, textBeforeCursor, cursorPosition, selection, range);
    }
    
    return false;
  };
  
  // Process a slash command
  const processSlashCommand = (
    command: string, 
    node: Node, 
    textBeforeCursor: string, 
    cursorPosition: number, 
    selection: Selection, 
    range: Range
  ): boolean => {
    // Calculate the start position of the command
    const commandStart = textBeforeCursor.lastIndexOf(command);
    
    // Create a range to replace the command
    const commandRange = document.createRange();
    commandRange.setStart(node, commandStart);
    commandRange.setEnd(node, cursorPosition);
    
    if (command === '/todo') {
      // Execute the todo command - add a checkbox
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.style.marginRight = '5px';
      checkbox.className = 'note-checkbox';
      
      // Add change event listener to the checkbox
      checkbox.addEventListener('change', () => {
        // Ensure the 'checked' attribute reflects the checkbox state
        if (checkbox.checked) {
          checkbox.setAttribute('checked', 'checked');
        } else {
          checkbox.removeAttribute('checked');
        }
        
        // Save content when checkbox is toggled
        if (noteInputRef.current) {
          setNoteContent(noteInputRef.current.innerHTML);
          localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
        }
      });
      
      // Replace the command with the checkbox
      commandRange.deleteContents();
      commandRange.insertNode(checkbox);
      
      // Move cursor after the checkbox
      const newRange = document.createRange();
      newRange.setStartAfter(checkbox);
      newRange.setEndAfter(checkbox);
      selection.removeAllRanges();
      selection.addRange(newRange);
      
      // Add space after checkbox if needed
      document.execCommand('insertText', false, ' ');
    } else if (command === '/prompt') {
      // Get the entire notepad content
      if (!noteInputRef.current) {
        return false;
      }
      
      // Get the full notepad content, regardless of node structure
      const fullContent = noteInputRef.current.innerText;
      
      // Find the command in the full content
      const commandIndex = fullContent.lastIndexOf(command);
      let promptText = '';
      
      if (commandIndex > 0) {
        // Extract everything before the command
        promptText = fullContent.substring(0, commandIndex).trim();
        console.log("Full notepad prompt text (tab completion):", promptText);
      }
      
      // Delete the slash command
      range.deleteContents();
      
      if (promptText) {
        // Insert a loading indicator on a new line
        const loadingSpan = document.createElement('span');
        loadingSpan.className = 'prompt-loading';
        loadingSpan.textContent = '\nGenerating response...';
        range.insertNode(loadingSpan);
        
        // Set cursor after the loading span
        const newRange = document.createRange();
        newRange.setStartAfter(loadingSpan);
        newRange.setEndAfter(loadingSpan);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Get AI response using the saved prompt text
        generateChatCompletion([
          { role: 'system', content: 'You are a helpful assistant. Provide a concise response.' },
          { role: 'user', content: promptText }
        ]).then(response => {
          if (response.success && response.data) {
            // Replace the loading indicator with the AI response
            if (noteInputRef.current) {
              // Clear the entire content only AFTER we have the response
              noteInputRef.current.innerHTML = '';
              
              // Format the response text and convert [] to checkboxes
              const responseContent = convertBracketsToCheckboxes(response.data.content);
              // First convert newlines to <br> tags, then add AI prefix
              const aiResponseText = `${responseContent.replace(/\n/g, '<br>')}`;
              
              // Insert the AI response as the only content using HTML to preserve checkbox elements
              noteInputRef.current.innerHTML = aiResponseText;
              
              // Make sure checkboxes have proper event listeners
              if (noteInputRef.current) {
                const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
                checkboxes.forEach(checkbox => {
                  checkbox.addEventListener('change', () => {
                    // Ensure the 'checked' attribute reflects the checkbox state
                    if ((checkbox as HTMLInputElement).checked) {
                      checkbox.setAttribute('checked', 'checked');
                    } else {
                      checkbox.removeAttribute('checked');
                    }
                    
                    // Save content when checkbox is toggled
                    if (noteInputRef.current) {
                      setNoteContent(noteInputRef.current.innerHTML);
                      localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
                    }
                  });
                });
              }
              
              // Update the note content
              setNoteContent(noteInputRef.current.innerHTML);
              localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
            }
          } else {
            // If there's an error, update the loading text
            loadingSpan.textContent = `Error: ${response.message || 'Failed to get AI response'}`;
            loadingSpan.style.color = '#ff6b6b';
            
            // Update the note content
            if (noteInputRef.current) {
              setNoteContent(noteInputRef.current.innerHTML);
              localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
            }
          }
        });
      } else {
        // If there's no prompt text, insert an error message
        document.execCommand('insertText', false, 'Error: Please write your prompt before the /prompt command');
      }
    }
    
    // Update note content
    if (noteInputRef.current) {
      setNoteContent(noteInputRef.current.innerHTML);
    }
    
    // Clear any active slash command suggestion
    setSlashCommand(null);
    
    return true;
  };
  
  // Handle keydown for slash commands
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Enter key for slash command execution
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      const node = range.startContainer;
      
      // We're only interested in text nodes
      if (node.nodeType !== Node.TEXT_NODE) return;
      
      const text = node.textContent || '';
      const cursorPosition = range.startOffset;
      const textBeforeCursor = text.substring(0, cursorPosition);
      
      // Check for slash commands without a space (immediate execution on Enter)
      const todoCommandMatch = /\/todo$/.exec(textBeforeCursor);
      const promptCommandMatch = /\/prompt$/.exec(textBeforeCursor);
      
      if (todoCommandMatch || promptCommandMatch) {
        e.preventDefault(); // Prevent the default Enter behavior
        
        const command = todoCommandMatch ? '/todo' : '/prompt';
        
        // Process the command
        processSlashCommand(command, node, textBeforeCursor, cursorPosition, selection, range);
        return;
      }
    }

    // Handle Tab key for command completion
    if (e.key === 'Tab' && slashCommand) {
      e.preventDefault(); // Prevent default tab behavior

      // Get selection and command info
      const selection = window.getSelection();
      if (selection && slashCommand.range && noteInputRef.current) {
        // Set selection to the slash command range to replace it
        selection.removeAllRanges();
        selection.addRange(slashCommand.range);
        
        // Check which command we're completing
        if (slashCommand.suggestion === '/todo') {
          // We'll directly replace the command with a checkbox
          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.className = 'note-checkbox';
          
          // Add change event listener to the checkbox
          checkbox.addEventListener('change', () => {
            // Ensure the 'checked' attribute reflects the checkbox state
            if (checkbox.checked) {
              checkbox.setAttribute('checked', 'checked');
            } else {
              checkbox.removeAttribute('checked');
            }
            
            // Save content when checkbox is toggled
            if (noteInputRef.current) {
              setNoteContent(noteInputRef.current.innerHTML);
              localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
            }
          });
          
          // Delete the slash command
          slashCommand.range.deleteContents();
          
          // Insert the checkbox
          slashCommand.range.insertNode(checkbox);
          
          // Move cursor after the checkbox
          const newRange = document.createRange();
          newRange.setStartAfter(checkbox);
          newRange.setEndAfter(checkbox);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          // Add space after checkbox
          document.execCommand('insertText', false, ' ');
        } else if (slashCommand.suggestion === '/prompt') {
          // Check if we have valid range
          if (!slashCommand.range) {
            console.error('Tab completion failed: slashCommand.range is null');
            return;
          }
          
          // Get the entire notepad content
          if (!noteInputRef.current) {
            return;
          }
          
          // Get the full notepad content, regardless of node structure
          const fullContent = noteInputRef.current.innerText;
          
          // Find the command in the full content
          const commandIndex = fullContent.lastIndexOf(slashCommand.command);
          let promptText = '';
          
          if (commandIndex > 0) {
            // Extract everything before the command
            promptText = fullContent.substring(0, commandIndex).trim();
            console.log("Full notepad prompt text (tab completion):", promptText);
          }
          
          // Delete the slash command
          slashCommand.range.deleteContents();
          
          if (promptText) {
            // Insert a loading indicator on a new line
            const loadingSpan = document.createElement('span');
            loadingSpan.className = 'prompt-loading';
            loadingSpan.textContent = '\nGenerating response...';
            slashCommand.range.insertNode(loadingSpan);
            
            // Set cursor after the loading span
            const newRange = document.createRange();
            newRange.setStartAfter(loadingSpan);
            newRange.setEndAfter(loadingSpan);
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            // Get AI response using the saved prompt text
            generateChatCompletion([
              { role: 'system', content: 'You are a helpful assistant. Provide a concise response.' },
              { role: 'user', content: promptText }
            ]).then(response => {
              if (response.success && response.data) {
                // Replace the loading indicator with the AI response
                if (noteInputRef.current) {
                  // Clear the entire content only AFTER we have the response
                  noteInputRef.current.innerHTML = '';
                  
                  // Format the response text and convert [] to checkboxes
                  const responseContent = convertBracketsToCheckboxes(response.data.content);
                  // First convert newlines to <br> tags, then add AI prefix
                  const aiResponseText = `${responseContent.replace(/\n/g, '<br>')}`;
                  
                  // Insert the AI response as the only content using HTML to preserve checkbox elements
                  noteInputRef.current.innerHTML = aiResponseText;
                  
                  // Make sure checkboxes have proper event listeners
                  if (noteInputRef.current) {
                    const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
                    checkboxes.forEach(checkbox => {
                      checkbox.addEventListener('change', () => {
                        // Ensure the 'checked' attribute reflects the checkbox state
                        if ((checkbox as HTMLInputElement).checked) {
                          checkbox.setAttribute('checked', 'checked');
                        } else {
                          checkbox.removeAttribute('checked');
                        }
                        
                        // Save content when checkbox is toggled
                        if (noteInputRef.current) {
                          setNoteContent(noteInputRef.current.innerHTML);
                          localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
                        }
                      });
                    });
                  }
                  
                  // Update the note content
                  setNoteContent(noteInputRef.current.innerHTML);
                  localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
                }
              } else {
                // If there's an error, update the loading text
                loadingSpan.textContent = `Error: ${response.message || 'Failed to get AI response'}`;
                loadingSpan.style.color = '#ff6b6b';
                
                // Update the note content
                if (noteInputRef.current) {
                  setNoteContent(noteInputRef.current.innerHTML);
                  localStorage.setItem('vibeNote', noteInputRef.current.innerHTML);
                }
              }
            });
          } else {
            // If there's no prompt text, insert an error message
            document.execCommand('insertText', false, 'Error: Please write your prompt before the /prompt command');
          }
        }
        
        // Update note content state
        setNoteContent(noteInputRef.current.innerHTML);
        
        // Clear the suggestion
        setSlashCommand(null);
      }
      return;
    }
    
    // Handle other keys like escape to cancel suggestion
    if (e.key === 'Escape' && slashCommand) {
      setSlashCommand(null);
      return;
    }
    
    // Track slash commands
    const trackSlashCommand = () => {
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        
        // We only care about text nodes
        if (node.nodeType !== 3) return;
        
        const text = node.textContent || '';
        const cursorPosition = range.startOffset;
        
        // Get text before cursor
        const textBeforeCursor = text.substring(0, cursorPosition);
        
        // Check for slash command in progress
        const slashIndex = textBeforeCursor.lastIndexOf('/');
        if (slashIndex !== -1 && (slashIndex === 0 || textBeforeCursor[slashIndex - 1].match(/\s/))) {
          const command = textBeforeCursor.substring(slashIndex);
          
          // Only process if command doesn't have a space (not completed yet)
          if (!command.includes(' ')) {
            // Get cursor position for overlay
            const rect = range.getBoundingClientRect();
            const editorRect = noteInputRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
            
            // Calculate position relative to editor
            const cursorX = rect.left - editorRect.left;
            const cursorY = rect.top - editorRect.top + (rect.height / 2);
            
            // Check for possible suggestions - only if the letters match exactly
            if (command === '/' || 
                command === '/t' || 
                command === '/to' || 
                command === '/tod') {
              // Save the command state
              const commandRange = document.createRange();
              commandRange.setStart(node, slashIndex);
              commandRange.setEnd(node, cursorPosition);
              setSlashCommand({ 
                command, 
                suggestion: '/todo', 
                range: commandRange,
                position: { x: cursorX, y: cursorY }
              });
            } else if (command === '/p' ||
                      command === '/pr' ||
                      command === '/pro' ||
                      command === '/prom' ||
                      command === '/promp') {
              // Save the command state for prompt
              const commandRange = document.createRange();
              commandRange.setStart(node, slashIndex);
              commandRange.setEnd(node, cursorPosition);
              setSlashCommand({ 
                command, 
                suggestion: '/prompt', 
                range: commandRange,
                position: { x: cursorX, y: cursorY }
              });
            } else {
              setSlashCommand(null);
            }
            return;
          }
        }
        
        // If we got here, there's no active command
        setSlashCommand(null);
      }, 0);
    };
    
    // Check for relevant keys
    if (['/','t','o','d','p','r','m','Backspace'].includes(e.key)) {
      trackSlashCommand();
    }
  };
  
  // Handle paste events to convert URLs to links
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    // Get selection and clipboard data
    const selection = window.getSelection();
    const clipboardData = e.clipboardData;
    const pastedText = clipboardData.getData('text/plain');
    
    // Check if selection exists and is within our editor
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      // Check if pasted text is a URL
      const urlRegex = /^(https?:\/\/[^\s]+)$/;
      if (urlRegex.test(pastedText)) {
        // If there's selected text, make it a link with the pasted URL
        if (!range.collapsed) {
          const selectedText = range.toString();
          
          // Create a link with the selected text and pasted URL
          const linkNode = document.createElement('a');
          linkNode.href = pastedText;
          linkNode.target = '_blank';
          linkNode.rel = 'noopener noreferrer';
          linkNode.textContent = selectedText;
          
          // Replace the selection with the link
          range.deleteContents();
          range.insertNode(linkNode);
          
          // Move the cursor to the end of the link
          selection.removeAllRanges();
          const newRange = document.createRange();
          newRange.setStartAfter(linkNode);
          newRange.setEndAfter(linkNode);
          selection.addRange(newRange);
        } else {
          // If no text is selected, just insert the URL as a clickable link
          const linkNode = document.createElement('a');
          linkNode.href = pastedText;
          linkNode.target = '_blank';
          linkNode.rel = 'noopener noreferrer';
          linkNode.textContent = pastedText;
          
          range.deleteContents();
          range.insertNode(linkNode);
          
          // Move cursor to end
          selection.removeAllRanges();
          const newRange = document.createRange();
          newRange.setStartAfter(linkNode);
          newRange.setEndAfter(linkNode);
          selection.addRange(newRange);
        }
      } else {
        // For non-URLs, just insert the plain text
        document.execCommand('insertText', false, pastedText);
      }
      
      // Update state with the new content
      if (noteInputRef.current) {
        setNoteContent(noteInputRef.current.innerHTML);
        setHasNote(noteInputRef.current.innerHTML.trim().length > 0);
      }
    }
  };

  // Handle clicking on elements in the note
  const handleNoteClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    
    // Handle checkbox clicks
    if (target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox') {
      // Let the default checkbox behavior work
      // but update the note content after the state change
      setTimeout(() => {
        if (noteInputRef.current) {
          // Ensure all checkbox states are properly reflected in their attributes
          const checkboxes = noteInputRef.current.querySelectorAll('input[type="checkbox"]');
          checkboxes.forEach(checkbox => {
            // Cast checkbox to HTMLInputElement to access 'checked' property
            const inputCheckbox = checkbox as HTMLInputElement;
            if (inputCheckbox.checked) {
              checkbox.setAttribute('checked', 'checked');
            } else {
              checkbox.removeAttribute('checked');
            }
          });
          
          const updatedContent = noteInputRef.current.innerHTML;
          setNoteContent(updatedContent);
          // Also immediately save to localStorage to ensure checkbox state is saved
          localStorage.setItem('vibeNote', updatedContent);
        }
      }, 0);
      return;
    }
    
    // Check if the clicked element is a link or a child of a link
    let element = target;
    let isLink = false;
    
    // Check if the element or any of its parents is an anchor
    while (element && !isLink) {
      if (element.tagName === 'A') {
        isLink = true;
        
        // Get the href attribute
        const href = element.getAttribute('href');
        if (href) {
          // Open the link in a new tab
          window.open(href, '_blank', 'noopener,noreferrer');
          
          // Prevent default to avoid double-opening
          e.preventDefault();
        }
      }
      element = element.parentElement as HTMLElement;
    }
  };

  // Function to convert [] patterns to checkboxes in text content
  const convertBracketsToCheckboxes = (content: string): string => {
    // First convert standalone brackets patterns
    let result = content.replace(/\[\]\s+([^\n]+)/g, '<input type="checkbox" class="note-checkbox">&nbsp;$1');
    
    // Then handle numbered list items with brackets
    result = result.replace(/(\d+\.)\s*\[\]\s+([^\n]+)/g, '$1 <input type="checkbox" class="note-checkbox">&nbsp;$2');
    
    // Handle bullet points with brackets
    result = result.replace(/([-•])\s*\[\]\s+([^\n]+)/g, '$1 <input type="checkbox" class="note-checkbox">&nbsp;$2');
    
    return result;
  };

  return (
    <div className="notes-tab">
      {/* Tool Header Buttons */}
      <ToolHeader
        isPinned={isPinned}
        onTogglePin={onTogglePin}
        isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen}
        toolType="note"
      />
      
      {/* Allow content to be shown during walkthrough or if user has subscription */}
      {(isWalkthroughMode || (user && hasPaidSubscription)) ? (
        <div className="note-editor-container">
          <div 
            ref={noteInputRef}
            contentEditable={!isTypingAnimation}
            onInput={handleContentEditableChange}
            onPaste={handlePaste}
            onClick={handleNoteClick}
            onKeyDown={handleKeyDown}
            data-placeholder="Write your thoughts here..."
            className="editable-note"
            spellCheck="false"
          />
          
          {/* Separate overlay for typewriter effect */}
          {isTypingAnimation && (
            <div 
              className="typewriter-overlay"
              dangerouslySetInnerHTML={{ __html: typewriterText.replace(/\n/g, '<br>') }}
            />
          )}
          
          {/* Suggestion overlay */}
          {slashCommand && (
            <div className="suggestion-overlay" style={{
              position: 'absolute',
              left: `${slashCommand.position.x - 2}px`,
              top: `${slashCommand.position.y}px`,
              transform: 'translateY(-50%)'
            }}>
              <span className="suggestion-text">
                <span className="suggestion-entered">{slashCommand.command}</span>
                <span className="suggestion-completion">{slashCommand.suggestion.substring(slashCommand.command.length)}</span>
              </span>
              <span className="suggestion-hint">Tab to complete</span>
            </div>
          )}
        </div>
      ) : (
        <div className="note-editor-container">
          <div className="editable-note locked">
            <p>Subscribe to unlock the notepad feature</p>
          </div>
          {/* Hidden div to preserve note content even when locked */}
          <div 
            style={{ display: 'none' }}
            ref={noteInputRef}
            dangerouslySetInnerHTML={{ __html: noteContent }}
          />
        </div>
      )}
    </div>
  );
};

export default NotesTab; 